import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface DeliveryNote {
  id: string;
  order_id: string;
  document_number: string;
  pdf_url?: string;
  status: 'pending' | 'generated' | 'sent';
  generated_by?: string;
  generated_at?: string;
  created_at: string;
  updated_at: string;
}

export const useDeliveryNotes = (orderId?: string) => {
  return useQuery({
    queryKey: ['delivery-notes', orderId],
    queryFn: async () => {
      const query = supabase
        .from('delivery_notes')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (orderId) {
        query.eq('order_id', orderId);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching delivery notes:", error);
        throw error;
      }

      // Return only the most recent delivery note per order
      return data && data.length > 0 ? [data[0]] as DeliveryNote[] : [] as DeliveryNote[];
    },
    enabled: !!orderId,
  });
};

export const useCreateDeliveryNote = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      orderId, 
      pdfBlob, 
      documentNumber 
    }: { 
      orderId: string; 
      pdfBlob: Blob; 
      documentNumber: string;
    }) => {
      // Check if delivery note already exists for this order
      const { data: existingNotes, error: checkError } = await supabase
        .from('delivery_notes')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (checkError) {
        console.error("Error checking existing delivery notes:", checkError);
        throw checkError;
      }

      // If delivery note exists, update it instead of creating new one
      if (existingNotes && existingNotes.length > 0) {
        const existingNote = existingNotes[0];
        
        // Upload new PDF (overwrite existing)
        const fileName = `delivery-note-${documentNumber}.pdf`;
        const { error: uploadError } = await supabase.storage
          .from('delivery-notes')
          .upload(fileName, pdfBlob, {
            contentType: 'application/pdf',
            upsert: true
          });

        if (uploadError) {
          console.error("Error uploading PDF:", uploadError);
          throw uploadError;
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('delivery-notes')
          .getPublicUrl(fileName);

        // Update existing delivery note record
        const { data, error } = await supabase
          .from('delivery_notes')
          .update({
            document_number: documentNumber,
            pdf_url: publicUrl,
            status: 'generated',
            generated_at: new Date().toISOString()
          })
          .eq('id', existingNote.id)
          .select()
          .single();

        if (error) {
          console.error("Error updating delivery note record:", error);
          throw error;
        }

        return data;
      }

      // No existing note, create new one
      const fileName = `delivery-note-${documentNumber}.pdf`;
      const { error: uploadError } = await supabase.storage
        .from('delivery-notes')
        .upload(fileName, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true
        });

      if (uploadError) {
        console.error("Error uploading PDF:", uploadError);
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('delivery-notes')
        .getPublicUrl(fileName);

      // Create delivery note record
      const { data, error } = await supabase
        .from('delivery_notes')
        .insert({
          order_id: orderId,
          document_number: documentNumber,
          pdf_url: publicUrl,
          status: 'generated'
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating delivery note record:", error);
        throw error;
      }

      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['delivery-notes', variables.orderId] });
      toast({
        title: "Success",
        description: "Delivery note generated successfully",
      });
    },
    onError: (error) => {
      console.error("Delivery note creation failed:", error);
      toast({
        title: "Error",
        description: "Failed to generate delivery note. Please try again.",
        variant: "destructive",
      });
    },
  });
};